import { FC, useState } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { useDebounce } from '@uidotdev/usehooks';
import intl from 'components/i18n/ReactIntlWrapper';

interface PersonaOption {
  id: number;
  label: string;
  name: string;
  entityType?: number; // Maps to EntityType table: 1=Employee, 2=Client, 3=Provider, 4=Corporate Entity
}

interface PersonaAutocompleteProps {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  label?: string;
  helperText?: string;
  error?: boolean;
  required?: boolean;
  disabled?: boolean;
  entityTypes?: number[]; // Filter by entity types: 1=Employee, 2=Client, 3=Provider, 4=Corporate Entity
}

const PersonaAutocomplete: FC<PersonaAutocompleteProps> = ({
  value,
  onChange,
  label = intl.get('accountManagement.persona'),
  helperText,
  error = false,
  required = false,
  disabled = false,
  entityTypes = [2, 3], // Default: Clients, Providers (for Account Management)
}) => {
  const { invoiceApi }: Api = useApi();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Fetch personas filtered by entity types
  const { data: personas, isLoading } = useQuery({
    queryKey: ['personas', entityTypes, debouncedSearchTerm],
    queryFn: async (): Promise<PersonaOption[]> => {
      const response = await invoiceApi.apiVversionPersonaPersonasByEntityTypesGet(
        '1',
        entityTypes
      );
      const personaList = response.data.data?.queryResult || [];
      
      // Filter by search term if provided
      let filtered = personaList;
      if (debouncedSearchTerm) {
        const lowerSearch = debouncedSearchTerm.toLowerCase();
        filtered = personaList.filter((persona: any) =>
          persona.name?.toLowerCase().includes(lowerSearch) ||
          persona.taxId?.toLowerCase().includes(lowerSearch)
        );
      }

      return filtered.map((persona: any) => ({
        id: persona.id,
        label: persona.name || '',
        name: persona.name || '',
        entityType: persona.entityType,
      }));
    },
    staleTime: 300000, // 5 minutes
    throwOnError: false,
  });

  // Fetch selected persona if value is provided
  const { data: selectedPersona } = useQuery({
    queryKey: ['persona', value],
    queryFn: async (): Promise<PersonaOption | null> => {
      if (!value) return null;
      const response = await invoiceApi.apiVversionPersonaIdGet(value, '1');
      const persona = response.data.data;
      if (persona) {
        return {
          id: persona.id!,
          label: persona.name || '',
          name: persona.name || '',
          entityType: persona.entityType as any as number,
        };
      }
      return null;
    },
    enabled: !!value,
    staleTime: 300000,
    throwOnError: false,
  });

  return (
    <Autocomplete
      value={selectedPersona || null}
      onChange={(_, newValue) => {
        onChange(newValue?.id);
      }}
      onInputChange={(_, newInputValue) => {
        setSearchTerm(newInputValue);
      }}
      options={personas || []}
      getOptionLabel={(option) => option.label}
      loading={isLoading}
      disabled={disabled}
      filterOptions={(x) => x} // Disable built-in filtering, we handle it server-side
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          placeholder={intl.get('common.placeholder.typeAhead.initialText')}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default PersonaAutocomplete;
